import { z } from 'zod';

import {
  LOCKER_KEY_INT_MAX,
  LockerKeyDisableReason,
  LockerKeyHolderType,
  LockerKeyReleaseReason,
  LockerKeyState,
  LockerKeyWarning,
} from '../enums/lockerKeys';
import { PickedCivilDayDto } from './civilDate';

const UuidDto = z.string().uuid();
const KeyNumberDto = z.coerce.number().int().min(1).max(LOCKER_KEY_INT_MAX);
const BodyKeyNumberDto = z.number().int().min(1).max(LOCKER_KEY_INT_MAX);
const GroupNameDto = z.string().trim().min(1).max(60);
const SearchTextDto = z.string().trim().min(1).max(255);
const IsoInstantDto = z.string().datetime({ offset: true });

/** The range rule create and the merged PATCH both apply (the service merges
 * the PATCH onto the stored row and calls this with the result). The size cap
 * is the service's coded RANGE_TOO_LARGE, not a schema error. */
export const isLockerKeyRangeOrdered = (numberFrom: number, numberTo: number) =>
  numberTo >= numberFrom;

// One-based page (staffHome convention). A page so deep that the row offset
// leaves the safe-integer range would reach SQL as an imprecise number.
const paginationShape = {
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
};
const hasSafeRowOffset = (query: { offset: number; limit: number }) =>
  Number.isSafeInteger((query.offset - 1) * query.limit);
const unsafeOffsetIssue = {
  message: 'VALIDATION__InvalidRange',
  path: ['offset'],
};

const queryBoolean = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true');

// [from, to): an empty or inverted window is a client bug.
const hasOrderedWindow = (query: { from?: string; to?: string }) =>
  !query.from || !query.to || Date.parse(query.from) < Date.parse(query.to);
const invalidWindowIssue = {
  message: 'VALIDATION__InvalidRange',
  path: ['to'],
};

export const CreateLockerKeyGroupDto = z
  .object({
    name: GroupNameDto,
    numberFrom: BodyKeyNumberDto,
    numberTo: BodyKeyNumberDto,
  })
  .strict()
  .refine((dto) => isLockerKeyRangeOrdered(dto.numberFrom, dto.numberTo), {
    message: 'VALIDATION__InvalidRange',
    path: ['numberTo'],
  });
export type ApiCreateLockerKeyGroupDto = z.infer<
  typeof CreateLockerKeyGroupDto
>;

export const UpdateLockerKeyGroupDto = z
  .object({
    name: GroupNameDto.optional(),
    numberFrom: BodyKeyNumberDto.optional(),
    numberTo: BodyKeyNumberDto.optional(),
    position: z.number().int().min(0).max(9999).optional(),
  })
  .strict()
  // `{ name: undefined }` survives parsing as an own key; it is still empty.
  .refine((dto) => Object.values(dto).some((value) => value !== undefined), {
    message: 'VALIDATION__Required',
  })
  // Only a complete pair can be checked here; a half-sent range is merged
  // with the stored row and re-validated by the service.
  .refine(
    (dto) =>
      dto.numberFrom === undefined ||
      dto.numberTo === undefined ||
      isLockerKeyRangeOrdered(dto.numberFrom, dto.numberTo),
    { message: 'VALIDATION__InvalidRange', path: ['numberTo'] },
  );
export type ApiUpdateLockerKeyGroupDto = z.infer<
  typeof UpdateLockerKeyGroupDto
>;

export const ListLockerKeyGroupsQueryDto = z
  .object({ includeArchived: queryBoolean.default('false') })
  .strict();
export type ApiListLockerKeyGroupsQueryDto = z.infer<
  typeof ListLockerKeyGroupsQueryDto
>;

export const LockerKeyBoardQueryDto = z
  .object({
    groupId: UuidDto,
    state: z.nativeEnum(LockerKeyState).optional(),
  })
  .strict();
export type ApiLockerKeyBoardQueryDto = z.infer<typeof LockerKeyBoardQueryDto>;

export const LockerKeySearchQueryDto = z
  .object({
    q: SearchTextDto,
    state: z.nativeEnum(LockerKeyState).optional(),
    ...paginationShape,
  })
  .strict()
  .refine(hasSafeRowOffset, unsafeOffsetIssue);
export type ApiLockerKeySearchQueryDto = z.infer<
  typeof LockerKeySearchQueryDto
>;

export const LockerKeyHoldersQueryDto = z
  .object({ q: SearchTextDto.optional(), ...paginationShape })
  .strict()
  .refine(hasSafeRowOffset, unsafeOffsetIssue);
export type ApiLockerKeyHoldersQueryDto = z.infer<
  typeof LockerKeyHoldersQueryDto
>;

const LockerKeyHolderDto = z.union([
  z.object({ userId: UuidDto }).strict(),
  z.object({ prospectId: UuidDto }).strict(),
]);

export const IssueLockerKeyDto = z
  .object({
    groupId: UuidDto,
    keyNumber: BodyKeyNumberDto,
    holder: LockerKeyHolderDto,
    validUntilDate: PickedCivilDayDto.optional(),
    checkInId: UuidDto.optional(),
    prospectTicketId: UuidDto.optional(),
    replaceCurrent: z.boolean().optional(),
    expectedCurrentAssignmentId: UuidDto.optional(),
  })
  .strict()
  .refine((dto) => !(dto.checkInId && dto.prospectTicketId), {
    message: 'VALIDATION__InvalidRange',
    path: ['prospectTicketId'],
  })
  // The expected id is the stale-confirmation guard of a Replace: required
  // with it, meaningless without it.
  .superRefine((dto, ctx) => {
    const replacing = dto.replaceCurrent === true;
    const hasExpected = dto.expectedCurrentAssignmentId !== undefined;
    if (replacing && !hasExpected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__Required',
        path: ['expectedCurrentAssignmentId'],
      });
    }
    if (!replacing && hasExpected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__InvalidRange',
        path: ['expectedCurrentAssignmentId'],
      });
    }
  });
export type ApiIssueLockerKeyDto = z.infer<typeof IssueLockerKeyDto>;

// Shared by both history endpoints: a number only exists inside a group.
const historyFilterShape = {
  groupId: UuidDto.optional(),
  keyNumber: KeyNumberDto.optional(),
  from: IsoInstantDto.optional(),
  to: IsoInstantDto.optional(),
  ...paginationShape,
};
const keyNumberNeedsGroupIssue = {
  message: 'VALIDATION__Required',
  path: ['groupId'],
};

export const LockerKeyAssignmentsQueryDto = z
  .object({
    ...historyFilterShape,
    holderUserId: UuidDto.optional(),
    holderProspectId: UuidDto.optional(),
  })
  .strict()
  .refine(
    (q) => q.keyNumber === undefined || q.groupId !== undefined,
    keyNumberNeedsGroupIssue,
  )
  .refine((q) => !(q.holderUserId && q.holderProspectId), {
    message: 'VALIDATION__InvalidRange',
    path: ['holderProspectId'],
  })
  .refine(hasOrderedWindow, invalidWindowIssue)
  .refine(hasSafeRowOffset, unsafeOffsetIssue);
export type ApiLockerKeyAssignmentsQueryDto = z.infer<
  typeof LockerKeyAssignmentsQueryDto
>;

export const DisableLockerKeyNumberDto = z
  .object({
    groupId: UuidDto,
    keyNumber: BodyKeyNumberDto,
    reason: z.nativeEnum(LockerKeyDisableReason),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type ApiDisableLockerKeyNumberDto = z.infer<
  typeof DisableLockerKeyNumberDto
>;

/**
 * Body of `POST assignments/:id/release` (Celina 2.14). Absent = the desk's
 * one-click release: the server records NOT_RETURNED for an overdue key and
 * RETURNED for today's. `{ reason: 'RETURNED' }` is „Zabeleži vraćanje”: the
 * key physically came back. REPLACED/DISABLED/NOT_RETURNED are written only by
 * the server's own flows, never chosen by a client.
 */
export const ReleaseLockerKeyDto = z
  .object({ reason: z.literal('RETURNED').optional() })
  .strict()
  .default({});
export type ApiReleaseLockerKeyDto = z.infer<typeof ReleaseLockerKeyDto>;

export const LockerKeyDisabledQueryDto = z
  .object(historyFilterShape)
  .strict()
  .refine(
    (q) => q.keyNumber === undefined || q.groupId !== undefined,
    keyNumberNeedsGroupIssue,
  )
  .refine(hasOrderedWindow, invalidWindowIssue)
  .refine(hasSafeRowOffset, unsafeOffsetIssue);
export type ApiLockerKeyDisabledQueryDto = z.infer<
  typeof LockerKeyDisabledQueryDto
>;

export interface ApiLockerKeyHolder {
  type: LockerKeyHolderType;
  id: string;
  name: string;
}
/**
 * A row of the Issue dialog's `/holders` picker: the holder, plus (Celina
 * 2.9) the member number of a USER holder as a CLIENT of this organization.
 * Null for a prospect and for an account that is not a numbered client here.
 * Historical holder payloads (`ApiLockerKeyHolder`) do not carry it.
 */
export interface ApiLockerKeyHolderOption extends ApiLockerKeyHolder {
  memberNumber: number | null;
}

export interface ApiLockerKeyGroup {
  id: string;
  name: string;
  numberFrom: number;
  numberTo: number;
  position: number;
  archivedAt: string | null;
}

/**
 * `overdueSinceDate` (Celina 2.14): the org-civil day (`yyyy-MM-dd`) an
 * unreleased key was issued on, when that day is before the organization's
 * today at the response's `asOf` — „nije vraćen od {date}”. Null for a key
 * issued today and for every released row. A key is never freed by time;
 * `validUntil` stays on the wire for compatibility only.
 */
export interface ApiLockerKeyCurrentAssignment {
  assignmentId: string;
  groupId: string;
  groupName: string;
  keyNumber: number;
  validUntil: string;
  overdueSinceDate: string | null;
}

export interface ApiLockerKeyAssignment {
  id: string;
  groupId: string;
  groupName: string;
  keyNumber: number;
  holder: ApiLockerKeyHolder;
  validUntil: string;
  issuedAt: string;
  issuedByName: string | null;
  releasedAt: string | null;
  releasedByName: string | null;
  releaseReason: LockerKeyReleaseReason | null;
  checkInId: string | null;
  prospectTicketId: string | null;
  /** See `ApiLockerKeyCurrentAssignment.overdueSinceDate`. */
  overdueSinceDate: string | null;
}

export interface ApiLockerKeyBoardRow {
  keyNumber: number;
  state: LockerKeyState;
  assignmentId: string | null;
  holder: ApiLockerKeyHolder | null;
  validUntil: string | null;
  issuedAt: string | null;
  /** See `ApiLockerKeyCurrentAssignment.overdueSinceDate`; null unless ISSUED. */
  overdueSinceDate: string | null;
  disabledNumberId: string | null;
  disableReason: LockerKeyDisableReason | null;
  disableNote: string | null;
}

export interface ApiLockerKeySearchRow extends ApiLockerKeyBoardRow {
  groupId: string;
  groupName: string;
}

export interface ApiLockerKeyDisabledNumber {
  id: string;
  groupId: string;
  groupName: string;
  keyNumber: number;
  reason: LockerKeyDisableReason;
  note: string | null;
  disabledAt: string;
  disabledByName: string | null;
  enabledAt: string | null;
  enabledByName: string | null;
}

/** `GET assignments/overdue-count`: every overdue assignment of the
 * selected organization, across all groups. */
export interface ApiLockerKeyOverdueCountResult {
  count: number;
}

/** `POST assignments/release-overdue`: the rows this call actually closed. */
export interface ApiReleaseOverdueLockerKeysResult {
  released: number;
}

export interface ApiIssueLockerKeyResult {
  assignment: ApiLockerKeyAssignment;
  warnings: LockerKeyWarning[];
}
